import React, { useState, useMemo, useCallback } from 'react';
import { FiSearch, FiFilter, FiChevronRight } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import { TopBar } from '@/design-system/components/organisms/TopBar';
import { DataTable, type DataTableColumn } from '@/features/onboarding/components/DataTable';
import { FilterBadge } from '@/features/onboarding/components/FilterBadge';
import {
  RejectedFilterModal,
  type RejectedFilterData,
} from '@/features/onboarding/components/RejectedFilterModal';
import { Sidebar } from '@/features/onboarding/components/Sidebar';
import { useRejectedClients, usePrefetchRejected } from '@/features/onboarding/hooks';
import type { PreClient, RejectedClientQueryParams } from '@/features/onboarding/types';
import { TypeAnalysis } from '@/features/onboarding/types';
import { ONBOARDING_SIDEBAR_ITEMS } from '@/shared/constants/sidebar.constants';

interface ActiveFilter {
  key: string;
  label: string;
  value: string;
}

export const RejectedPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  // Filter states
  const [dateStart, setDateStart] = useState<string | undefined>(undefined);
  const [dateEnd, setDateEnd] = useState<string | undefined>(undefined);
  const [typeDisaproved, setTypeDisaproved] = useState<number | undefined>(undefined);
  const [categoryRejectedName, setCategoryRejectedName] = useState<string | undefined>(undefined);

  // Build query params dynamically
  const queryParams = useMemo<RejectedClientQueryParams>(() => {
    const params: RejectedClientQueryParams = {
      page: currentPage,
      limit: itemsPerPage,
    };

    if (searchQuery.trim()) {
      params.search = searchQuery.trim();
    }

    if (dateStart) {
      params.dateStart = dateStart;
    }

    if (dateEnd) {
      params.dateEnd = dateEnd;
    }

    if (typeDisaproved) {
      params.typeDisaproved = typeDisaproved;
    }

    if (categoryRejectedName) {
      params.categoryRejectedName = categoryRejectedName;
    }

    return params;
  }, [
    currentPage,
    itemsPerPage,
    searchQuery,
    dateStart,
    dateEnd,
    typeDisaproved,
    categoryRejectedName,
  ]);

  // Fetch rejected clients with React Query
  const { data, isLoading, isError, error } = useRejectedClients(queryParams);

  // Prefetch next page
  const prefetchNextPage = usePrefetchRejected(queryParams);

  // Build active filters for display
  const activeFilters = useMemo<ActiveFilter[]>(() => {
    const filters: ActiveFilter[] = [];

    if (dateStart) {
      const formattedDate = new Date(dateStart).toLocaleDateString('pt-BR');
      filters.push({
        key: 'dateStart',
        label: `Data início: ${formattedDate}`,
        value: dateStart,
      });
    }

    if (dateEnd) {
      const formattedDate = new Date(dateEnd).toLocaleDateString('pt-BR');
      filters.push({
        key: 'dateEnd',
        label: `Data fim: ${formattedDate}`,
        value: dateEnd,
      });
    }

    if (typeDisaproved) {
      const typeLabel = typeDisaproved === TypeAnalysis.MANUAL ? 'Manual' : 'Automática';
      filters.push({
        key: 'typeDisaproved',
        label: `Tipo: ${typeLabel}`,
        value: String(typeDisaproved),
      });
    }

    if (categoryRejectedName) {
      filters.push({
        key: 'categoryRejectedName',
        label: `Categoria: ${categoryRejectedName}`,
        value: categoryRejectedName,
      });
    }

    return filters;
  }, [dateStart, dateEnd, typeDisaproved, categoryRejectedName]);

  // Handle filter removal
  const handleRemoveFilter = useCallback((filterKey: string) => {
    switch (filterKey) {
      case 'dateStart':
        setDateStart(undefined);
        break;
      case 'dateEnd':
        setDateEnd(undefined);
        break;
      case 'typeDisaproved':
        setTypeDisaproved(undefined);
        break;
      case 'categoryRejectedName':
        setCategoryRejectedName(undefined);
        break;
    }
    setCurrentPage(1);
  }, []);

  // Handle filter application from modal
  const handleApplyFilters = useCallback((filterData: RejectedFilterData) => {
    if (filterData.startDate) {
      const date = new Date(filterData.startDate);
      setDateStart(date.toISOString().split('T')[0]);
    } else {
      setDateStart(undefined);
    }

    if (filterData.endDate) {
      const date = new Date(filterData.endDate);
      setDateEnd(date.toISOString().split('T')[0]);
    } else {
      setDateEnd(undefined);
    }

    if (filterData.typeDisaproved) {
      setTypeDisaproved(filterData.typeDisaproved);
    } else {
      setTypeDisaproved(undefined);
    }

    if (filterData.categoryRejectedName) {
      setCategoryRejectedName(filterData.categoryRejectedName);
    } else {
      setCategoryRejectedName(undefined);
    }

    setCurrentPage(1);
    setIsFilterModalOpen(false);
  }, []);

  // Handle search
  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  }, []);

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    if (data?.pagination && page < data.pagination.totalPages) {
      prefetchNextPage();
    }
  };

  // Handle items per page change
  const handleItemsPerPageChange = useCallback((limit: number) => {
    setItemsPerPage(limit);
    setCurrentPage(1);
  }, []);

  const columns: DataTableColumn<PreClient>[] = [
    {
      key: 'name',
      label: 'Cliente',
      width: '18%',
    },
    {
      key: 'rejectionDate',
      label: 'Data da reprovação',
      width: '15%',
      render: (value: unknown) => {
        if (!value) return '-';
        const date = new Date(value as string);
        return date.toLocaleDateString('pt-BR');
      },
    },
    {
      key: 'reasonRejection',
      label: 'Motivo',
      width: '20%',
      render: (value: unknown) => (value as string) || '-',
    },
    {
      key: 'userAnalystName',
      label: 'Usuário',
      width: '15%',
      render: (value: unknown) => (value as string) || '-',
    },
    {
      key: 'isBlacklisted',
      label: 'Blocklist',
      width: '10%',
      render: (value: unknown) => (value ? 'Sim' : 'Não'),
    },
    {
      key: 'typeAnalysis',
      label: 'Reprovação',
      width: '12%',
      render: (value: unknown) => {
        const type = value as number | null;
        const isManual = type === TypeAnalysis.MANUAL;
        const isAutomatic = type === TypeAnalysis.AUTOMATIC;
        const displayType = isManual ? 'Manual' : isAutomatic ? 'Automática' : '-';
        return (
          <span
            className={`px-3 py-1 rounded-full text-xs font-medium ${
              isAutomatic
                ? 'bg-purple-100 text-purple-700'
                : isManual
                  ? 'bg-orange-100 text-orange-700'
                  : 'bg-gray-100 text-gray-700'
            }`}
          >
            {displayType}
          </span>
        );
      },
    },
    {
      key: 'actions',
      label: 'Ações',
      width: '10%',
      render: (_: unknown, row: PreClient) => (
        <button
          type="button"
          onClick={() => {
            void navigate(`/onboarding/rejected/client/${row.id}`);
          }}
          className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
        >
          <FiChevronRight className="w-5 h-5" />
        </button>
      ),
    },
  ];

  // Get data from API response
  const clients = data?.data ?? [];
  const pagination = data?.pagination ?? { page: 1, limit: 10, totalItems: 0, totalPages: 1 };

  return (
    <div className="min-h-screen bg-neutral-50">
      <TopBar />

      <div className="flex">
        {/* Sidebar */}
        <Sidebar items={ONBOARDING_SIDEBAR_ITEMS} className="w-64 min-h-[calc(100vh-140px)]" />

        {/* Main Content */}
        <main className="flex-1 p-8">
          <div className="max-w-7xl mx-auto">
            {/* Search and Filters */}
            <div className="flex items-center gap-4 mb-4">
              {/* Search */}
              <div className="flex-1 relative">
                <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Buscar por cliente ou CPF/CNPJ"
                  value={searchQuery}
                  onChange={(e) => {
                    handleSearchChange(e.target.value);
                  }}
                  className="w-full pl-12 pr-4 py-3 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Filter Button */}
              <button
                type="button"
                onClick={() => {
                  setIsFilterModalOpen(true);
                }}
                className="flex items-center gap-2 px-6 py-3 bg-white border-2 border-purple-600 text-neutral-700 rounded-lg hover:bg-purple-50 transition-colors font-medium"
              >
                <FiFilter className="w-5 h-5 text-neutral-600" />
                Filtros
              </button>
            </div>

            {/* Active Filters */}
            {activeFilters.length > 0 && (
              <div className="flex items-center gap-2 mb-6 flex-wrap">
                {activeFilters.map((filter) => (
                  <FilterBadge
                    key={filter.key}
                    label={filter.label}
                    onRemove={() => {
                      handleRemoveFilter(filter.key);
                    }}
                  />
                ))}
              </div>
            )}

            {/* Loading State */}
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                <span className="ml-3 text-neutral-600">Carregando clientes reprovados...</span>
              </div>
            )}

            {/* Error State */}
            {isError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                <p className="font-medium">Erro ao carregar clientes reprovados</p>
                <p className="text-sm">
                  {error instanceof Error ? error.message : 'Tente novamente mais tarde.'}
                </p>
              </div>
            )}

            {/* Data Table */}
            {!isLoading && !isError && (
              <DataTable
                columns={columns}
                data={clients}
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                totalItems={pagination.totalItems}
                itemsPerPage={pagination.limit}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            )}
          </div>
        </main>
      </div>

      {/* Filter Modal */}
      <RejectedFilterModal
        isOpen={isFilterModalOpen}
        onClose={() => {
          setIsFilterModalOpen(false);
        }}
        onApply={handleApplyFilters}
      />
    </div>
  );
};
